import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import theme from './theme/theme';
import { AuthProvider, useAuth } from './context/AuthContext';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import PrivateRoute from './components/PrivateRoute';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import CreateSurvey from './pages/CreateSurvey';
import SurveyLibrary from './pages/SurveyLibrary';
import AllCurrentSurveys from './pages/AllCurrentSurveys';
import AllCompletedSurveys from './pages/AllCompletedSurveys';
import TakeSurvey from './pages/TakeSurvey';
import SurveyResponses from './pages/SurveyResponses';
import UserManagement from './pages/UserManagement';
import OrganizationManagement from './pages/OrganizationManagement';
import OrganizationUsers from './pages/OrganizationUsers';
import TeamManagement from './pages/TeamManagement';
import TeamDetails from './pages/TeamDetails';
import Profile from './pages/Profile';
import OneToOnes from './pages/OneToOnes';
import OneToOnesCompleted from './pages/OneToOnesCompleted';
import Landing from './pages/Landing';
import SurveyComplete from './pages/SurveyComplete';
import WelcomeSetPassword from './pages/WelcomeSetPassword';
import './styles/public.css';
import './styles/mobile.css';
import Platform from './pages/public/Platform';
import Industries from './pages/public/Industries';
import Contact from './pages/public/Contact';
import HR from './pages/public/HR';
import Resources from './pages/Resources';
import ResourceDetail from './pages/ResourceDetail';
import Community from './pages/Community';
import CommunityDetail from './pages/CommunityDetail';
import CommunitySubmit from './pages/CommunitySubmit';
import CommunitySubmitSuccess from './pages/CommunitySubmitSuccess';
import CommunityMe from './pages/CommunityMe';
import CommunityAdmin from './pages/CommunityAdmin';

function DashboardRoute() {
  const { user } = useAuth();

  if (user?.role === 'community') {
    return <Navigate to="/community/me" replace />;
  }

  return <Dashboard />;
}

function Shell() {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  const isPublicCommunityPage =
    location.pathname === '/community' ||
    location.pathname === '/community/submit' ||
    location.pathname === '/community/submit/success' ||
    /^\/community\/(?!me$)(?!admin$)[^/]+$/.test(location.pathname);
  const isPublicPage =
    ['/', '/platform', '/industries', '/contact', '/hr', '/resources'].includes(location.pathname) ||
    location.pathname.startsWith('/resources/') ||
    isPublicCommunityPage;
  const showSidebar = Boolean(isAuthenticated && (user?.role === 'manager' || user?.role === 'admin') && !isPublicPage);
  const isTakingSurvey = /^\/surveys\/[^/]+\/take$/.test(location.pathname);
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);
  // Hide navbar for employee while taking survey, and for manager/admin app (when sidebar is visible)
  // Always show navbar on public pages
  const hideNavbar = ((isTakingSurvey && user?.role === 'employee') || showSidebar) && !isPublicPage;
  const isEmployeeDashboard =
    isAuthenticated && user?.role === 'employee' && location.pathname.startsWith('/dashboard');
  return (
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        {showSidebar && (
          <Box sx={{ position: 'fixed', zIndex: (t) => t.zIndex.appBar - 1 }}>
            <Sidebar />
          </Box>
        )}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {!hideNavbar && <Navbar />}
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              bgcolor: 'background.default',
              pl: { md: showSidebar ? '240px' : 0 },
              pt: !hideNavbar && isEmployeeDashboard ? { xs: 7, sm: 8 } : 0,
            }}
          >
            <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/welcome" element={<WelcomeSetPassword />} />
            <Route path="/register" element={<Register />} />
            <Route path="/platform" element={<Platform />} />
            <Route path="/industries" element={<Industries />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/hr" element={<HR />} />

            <Route path="/community" element={<Community />} />
            <Route path="/community/submit" element={<CommunitySubmit />} />
            <Route path="/community/submit/success" element={<CommunitySubmitSuccess />} />
            <Route path="/community/:id" element={<CommunityDetail />} />

            <Route
              path="/community/me"
              element={
                <PrivateRoute requiredRole="community">
                  <CommunityMe />
                </PrivateRoute>
              }
            />

            <Route
              path="/community/admin"
              element={
                <PrivateRoute requiredRole="admin">
                  <CommunityAdmin />
                </PrivateRoute>
              }
            />
            
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <DashboardRoute />
                </PrivateRoute>
              }
            />
            
            <Route
              path="/surveys/create"
              element={
                <PrivateRoute requiredRole="manager">
                  <CreateSurvey />
                </PrivateRoute>
              }
            />
            
            <Route
              path="/surveys/:id/edit"
              element={
                <PrivateRoute requiredRole="manager">
                  <CreateSurvey />
                </PrivateRoute>
              }
            />
            
            <Route
              path="/surveys/:id/take"
              element={
                <PrivateRoute>
                  <TakeSurvey />
                </PrivateRoute>
              }
            />
            <Route
              path="/surveys/:id/complete"
              element={
                <PrivateRoute>
                  <SurveyComplete />
                </PrivateRoute>
              }
            />
            
            <Route
              path="/surveys/:id/responses"
              element={
                <PrivateRoute requiredRole="manager">
                  <SurveyResponses />
                </PrivateRoute>
              }
            />
            
            <Route
              path="/users"
              element={
                <PrivateRoute requiredRole="manager">
                  <UserManagement />
                </PrivateRoute>
              }
            />
            
            <Route
              path="/organizations"
              element={
                <PrivateRoute requiredRole="admin">
                  <OrganizationManagement />
                </PrivateRoute>
              }
            />
            
            <Route
              path="/organizations/:id/users"
              element={
                <PrivateRoute requiredRole="admin">
                  <OrganizationUsers />
                </PrivateRoute>
              }
            />
            
            <Route
              path="/teams"
              element={
                <PrivateRoute requiredRole="manager">
                  <TeamManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/teams/:id"
              element={
                <PrivateRoute requiredRole="manager">
                  <TeamDetails />
                </PrivateRoute>
              }
            />
            
            <Route
              path="/profile"
              element={
                <PrivateRoute>
                  <Profile />
                </PrivateRoute>
              }
            />

            <Route
              path="/resources"
              element={<Resources />}
            />

            <Route
              path="/resources/:id"
              element={<ResourceDetail />}
            />

            {/* Surveys index page (manager/admin) */}
            <Route
              path="/surveys"
              element={
                <PrivateRoute requiredRole="manager">
                  <Dashboard />
                </PrivateRoute>
              }
            />

            {/* All current surveys page */}
            <Route
              path="/surveys/current"
              element={
                <PrivateRoute requiredRole="manager">
                  <AllCurrentSurveys />
                </PrivateRoute>
              }
            />

            {/* All completed surveys page */}
            <Route
              path="/surveys/completed"
              element={
                <PrivateRoute requiredRole="manager">
                  <AllCompletedSurveys />
                </PrivateRoute>
              }
            />
            
            {/* Survey Library page */}
            <Route
              path="/surveys/library"
              element={
                <PrivateRoute>
                  <SurveyLibrary />
                </PrivateRoute>
              }
            />

            {/* Coming soon placeholders */}
            <Route
              path="/1-2-1s"
              element={
                <PrivateRoute requiredRole="manager">
                  <OneToOnes />
                </PrivateRoute>
              }
            />
            <Route
              path="/1-2-1s/completed"
              element={
                <PrivateRoute requiredRole="manager">
                  <OneToOnesCompleted />
                </PrivateRoute>
              }
            />
            <Route
              path="/goals"
              element={
                <PrivateRoute requiredRole="manager">
                  <div style={{ padding: 24 }}>Goals coming soon.</div>
                </PrivateRoute>
              }
            />
            <Route
              path="/workshops"
              element={
                <PrivateRoute requiredRole="manager">
                  <div style={{ padding: 24 }}>Workshops coming soon.</div>
                </PrivateRoute>
              }
            />

            {/* Messages placeholder (manager/admin) */}
            <Route
              path="/messages"
              element={
                <PrivateRoute requiredRole="manager">
                  <div style={{ padding: 24 }}>Messages coming soon.</div>
                </PrivateRoute>
              }
            />
            
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Box>
        </Box>
      </Box>
  );
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Shell />
        </Router>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
